/**
 * Simplified turnover V1 — MRG order (Slice 17):
 * Keys → Walk → Clean → Inventory → After → Drop keys → Done
 * Walk merges Before photos + Inspect issues.
 * Drop keys is end-of-job dropoff proof (separate from arrival Keys).
 */
import type { CleaningPhotoCaptureRecord } from "@/lib/cleaningPhotoCapture";

export const SIMPLE_TURNOVER_V1 = true;

/** Soft minimum photos before Start cleaning (Slice 17). */
export const WALK_MIN_PHOTOS = 3;

export type SimpleTurnoverPhase =
  | "keys"
  | "walk"
  | "before"
  | "inspect"
  | "clean"
  | "inventory"
  | "after"
  | "done";

export type RoomSkipReason = "locked" | "guest" | "unsafe";

/** One room's photos, whether the cleaner finished it, or why they skipped it. */
export type RoomShots = {
  photo_urls: string[];
  done?: boolean;
  skip?: RoomSkipReason;
  /** When the cleaner skipped the room (ISO), so the owner can see it. */
  skipped_at?: string;
  /** Slice 57 — the guided Walk shots, keyed by "room" (whole room) or the photo group id. */
  shots?: Record<string, GuidedShot>;
  /** Slice 57 — extra condition shots taken with "Take another photo". */
  extras?: { url: string; at: string }[];
};

/** One guided Walk photo: where it is, when it was taken, and when it was retaken (if it was). */
export type GuidedShot = { url: string; at: string; retaken_at?: string };

export type InspectFinding = {
  reference_photo_id?: string | null;
  reference_photo_url?: string | null;
  status: "ok" | "missing" | "broken";
  note?: string | null;
  evidence_photo_url?: string | null;
  /** Slice 53 — the room the cleaner was in when they flagged it. */
  room_name?: string | null;
};

export type SimpleFlowState = {
  simple_flow?: boolean;
  keys_complete?: boolean;
  /** Slice 17 — Walk (Before + Inspect) complete */
  walk_complete?: boolean;
  before_complete?: boolean;
  inspect_complete?: boolean;
  clean_complete?: boolean;
  /** Slice 50 — arrival checklist answers per task id (ticked, photos) and "Start the checklist" pressed */
  arrival_answers?: Record<string, { done?: boolean; photo_urls?: string[] }>;
  arrival_complete?: boolean;
  /** Slice 50 — cleaning checklist items ticked in Clean, keys are `${room}::${task text}` */
  clean_checklist_ticks?: string[];
  /** Slice 53 — Walk (before) and After photos kept per room, keyed by property room id. */
  walk_by_room?: Record<string, RoomShots>;
  after_by_room?: Record<string, RoomShots>;
  /** Slice 53 — After reference the cleaner picked, kept for the rest of After. */
  after_ref_tab?: "cover" | "before";
  /** Freeform before / Walk photo URLs */
  before_photo_urls?: string[];
  /** Freeform after photo URLs */
  after_photo_urls?: string[];
  /** Arrival keys photo */
  keys_photo_url?: string | null;
  /** Slice 18 — key dropoff photo (where keys were left) */
  keys_drop_photo_url?: string | null;
  keys_drop_complete?: boolean;
  /** Walk / Inspect findings (Missing / Broken) */
  inspect_findings?: InspectFinding[];
  /** Slice 36/42 — optional Quick check answers (must-test id → ok | problem) */
  walk_quick_checks?: Record<string, "ok" | "problem">;
  /** Slice 42 — evidence photo URL per quick-check id when Problem */
  walk_quick_check_photos?: Record<string, string>;
  /**
   * Slice 40 — due maintenance checks on this turnover (cleaner path).
   * Key = property_preventative_maintenance id.
   */
  maintenance_checks?: Record<
    string,
    { status: "done" | "skipped"; proof_url?: string | null; title?: string }
  >;
  /** True when every due cleaner check is done or skipped (or none due). */
  maintenance_complete?: boolean;
  /** In-app camera captures with claim metadata (time, GPS, hash). */
  photo_captures?: CleaningPhotoCaptureRecord[];
  /** Legacy */
  maintenance_complete?: boolean;
  maintenance_skipped?: boolean;
  before_media_by_standard_id?: Record<string, string[]>;
  after_media_by_standard_id?: Record<string, string[]>;
};

export function readSimpleFlowState(raw: unknown): SimpleFlowState {
  if (!raw || typeof raw !== "object") return {};
  return raw as SimpleFlowState;
}

/** Flatten freeform + legacy standard maps into one URL list */
export function collectSimplePhotoUrls(
  state: SimpleFlowState,
  phase: "before" | "after",
): string[] {
  const free = phase === "before" ? state.before_photo_urls : state.after_photo_urls;
  if (free && free.length > 0) return [...free];
  const map =
    phase === "before" ? state.before_media_by_standard_id : state.after_media_by_standard_id;
  if (!map) return [];
  return Object.values(map).flat().filter(Boolean);
}

/** Walk photos done when walk_complete is set, or legacy before+inspect both done. */
export function isWalkComplete(state: SimpleFlowState): boolean {
  if (state.walk_complete) return true;
  return !!state.before_complete && !!state.inspect_complete;
}

/** Slice 42 — every property quick-check answered (or none configured). */
export function isWalkQuickChecksComplete(
  state: SimpleFlowState,
  checkIds: string[],
): boolean {
  if (checkIds.length === 0) return true;
  const answers = state.walk_quick_checks ?? {};
  return checkIds.every((id) => answers[id] === "ok" || answers[id] === "problem");
}

/** Slice 18 — end-of-job key dropoff proof. */
export function isSimpleDropKeysComplete(state: SimpleFlowState): boolean {
  return !!state.keys_drop_complete && !!state.keys_drop_photo_url;
}

/** Slice 37 — short live label for VA desk / Schedule (canvas copy). */
export function simpleLivePhaseLabel(
  entryWalkthroughState: unknown,
  opts?: { walkQuickCheckIds?: string[] },
): string {
  const s = readSimpleFlowState(entryWalkthroughState);
  if (isSimpleDropKeysComplete(s) || s.keys_drop_photo_url || s.keys_drop_complete) {
    return "Drop keys";
  }
  if (s.clean_complete) return "After";
  if (isWalkComplete(s)) {
    const ids = opts?.walkQuickCheckIds ?? [];
    if (ids.length > 0 && !isWalkQuickChecksComplete(s, ids)) return "Check";
    return "Clean";
  }
  if (s.keys_complete) return "Walk";
  return "Keys";
}


/**
 * Local “job finished” for SIMPLE_TURNOVER_V1.
 * Checklist + inventory flags alone must NOT mean complete — drop keys is required.
 */
export function isSimpleTurnoverFullyDone(input: {
  entryWalkthroughState?: unknown;
  checklistCompleted?: boolean | null;
  inventoryCompleted?: boolean | null;
  includeInventoryCheck?: boolean | null;
  hasInventoryItems?: boolean;
  taskStatus?: string | null;
}): boolean {
  if (input.taskStatus === "completed") return true;
  const simple = readSimpleFlowState(input.entryWalkthroughState);
  if (!isSimpleDropKeysComplete(simple)) return false;
  if (!input.checklistCompleted) return false;
  const inventoryRequired =
    input.includeInventoryCheck !== false && !!input.hasInventoryItems;
  if (inventoryRequired && !input.inventoryCompleted) return false;
  return true;
}

/** Progress segments: Keys · Walk · [Check] · [Maint] · Clean · Inv · After · Drop */
export const SIMPLE_FLOW_SEGMENTS = [
  "keys",
  "walk",
  "clean_free",
  "inventory_single",
  "after_photos",
  "drop_keys",
] as const;

export type SimpleFlowSegmentId =
  | (typeof SIMPLE_FLOW_SEGMENTS)[number]
  | "walk_checks"
  | "maintenance"
  | "guest_setup";

/** Chrome flags for the progress strip / rewind math. */
export type SimpleFlowChromeOpts = {
  includeMaintenance?: boolean;
  includeQuickCheck?: boolean;
  /** Slice 44 — Guest leave-out step when checklist staging exists. */
  includeGuestSetup?: boolean;
};

function normalizeChromeOpts(
  opts?: boolean | SimpleFlowChromeOpts,
): Required<SimpleFlowChromeOpts> {
  if (typeof opts === "boolean") {
    return { includeMaintenance: opts, includeQuickCheck: false, includeGuestSetup: false };
  }
  return {
    includeMaintenance: !!opts?.includeMaintenance,
    includeQuickCheck: !!opts?.includeQuickCheck,
    includeGuestSetup: !!opts?.includeGuestSetup,
  };
}

/** Labels for progress strip — Check / Maint / Guest only when present for this job. */
export function simpleFlowSegmentLabels(opts?: boolean | SimpleFlowChromeOpts): string[] {
  const o = normalizeChromeOpts(opts);
  const labels = ["Keys", "Walk"];
  if (o.includeQuickCheck) labels.push("Check");
  if (o.includeMaintenance) labels.push("Maint");
  labels.push("Clean");
  if (o.includeGuestSetup) labels.push("Guest");
  labels.push("Inv", "After", "Drop");
  return labels;
}

export function simpleFlowSegments(opts?: boolean | SimpleFlowChromeOpts): SimpleFlowSegmentId[] {
  const o = normalizeChromeOpts(opts);
  const segs: SimpleFlowSegmentId[] = ["keys", "walk"];
  if (o.includeQuickCheck) segs.push("walk_checks");
  if (o.includeMaintenance) segs.push("maintenance");
  segs.push("clean_free");
  if (o.includeGuestSetup) segs.push("guest_setup");
  segs.push("inventory_single", "after_photos", "drop_keys");
  return segs;
}

export function simpleSegmentIdFromIndex(
  index: number,
  opts?: boolean | SimpleFlowChromeOpts,
): SimpleFlowSegmentId | null {
  const segs = simpleFlowSegments(opts);
  if (index < 0 || index >= segs.length) return null;
  return segs[index]!;
}

/** True when every due cleaner maintenance id is done or skipped. */
export function isMaintenanceChecksComplete(
  state: SimpleFlowState,
  dueIds: string[],
): boolean {
  if (dueIds.length === 0) return true;
  if (state.maintenance_complete) return true;
  const map = state.maintenance_checks ?? {};
  return dueIds.every((id) => {
    const row = map[id];
    return row?.status === "done" || row?.status === "skipped";
  });
}

export function skippedMaintenanceTitles(state: SimpleFlowState): string[] {
  const map = state.maintenance_checks ?? {};
  return Object.values(map)
    .filter((r) => r.status === "skipped")
    .map((r) => r.title?.trim() || "Maintenance")
    .filter(Boolean);
}

/** Previous segment for Back — skips Inventory when that step is auto-skipped. */
export function previousSimpleSegmentIndex(
  activeIndex: number,
  opts?: {
    inventorySkipped?: boolean;
    includeMaintenance?: boolean;
    includeQuickCheck?: boolean;
  },
): number {
  if (activeIndex <= 0) return -1;
  const segs = simpleFlowSegments(opts);
  const invIndex = segs.indexOf("inventory_single");
  const cleanIndex = segs.indexOf("clean_free");
  const guestIndex = segs.indexOf("guest_setup");
  if (opts?.inventorySkipped && activeIndex === invIndex + 1) {
    if (guestIndex >= 0) return guestIndex;
    return cleanIndex;
  }
  return activeIndex - 1;
}

/**
 * Rewind task state so `target` becomes the first incomplete wizard step.
 * Keeps media; only clears completion flags from target onward.
 */
export function buildSimpleFlowRewind(target: SimpleFlowSegmentId): {
  flowPatch: Partial<SimpleFlowState>;
  clearChecklistCompleted: boolean;
  clearInventoryCompleted: boolean;
  clearGuestSetupCompleted: boolean;
} {
  const flowPatch: Partial<SimpleFlowState> = { simple_flow: true };
  let clearChecklistCompleted = false;
  let clearInventoryCompleted = false;
  let clearGuestSetupCompleted = false;

  const clearFromWalk = () => {
    flowPatch.walk_complete = false;
    flowPatch.before_complete = false;
    flowPatch.inspect_complete = false;
  };
  const clearFromWalkChecks = () => {
    flowPatch.walk_quick_checks = {};
    flowPatch.walk_quick_check_photos = {};
  };
  const clearFromMaintenance = () => {
    flowPatch.maintenance_complete = false;
    flowPatch.maintenance_checks = {};
  };
  const clearFromClean = () => {
    flowPatch.clean_complete = false;
  };
  const clearFromDrop = () => {
    flowPatch.keys_drop_complete = false;
  };

  switch (target) {
    case "keys":
      flowPatch.keys_complete = false;
      clearFromWalk();
      clearFromWalkChecks();
      clearFromMaintenance();
      clearFromClean();
      clearFromDrop();
      clearChecklistCompleted = true;
      clearInventoryCompleted = true;
      clearGuestSetupCompleted = true;
      break;
    case "walk":
      clearFromWalk();
      clearFromWalkChecks();
      clearFromMaintenance();
      clearFromClean();
      clearFromDrop();
      clearChecklistCompleted = true;
      clearInventoryCompleted = true;
      clearGuestSetupCompleted = true;
      break;
    case "walk_checks":
      clearFromWalkChecks();
      clearFromMaintenance();
      clearFromClean();
      clearFromDrop();
      clearChecklistCompleted = true;
      clearInventoryCompleted = true;
      clearGuestSetupCompleted = true;
      break;
    case "maintenance":
      clearFromMaintenance();
      clearFromClean();
      clearFromDrop();
      clearChecklistCompleted = true;
      clearInventoryCompleted = true;
      clearGuestSetupCompleted = true;
      break;
    case "clean_free":
      clearFromClean();
      clearFromDrop();
      clearChecklistCompleted = true;
      clearInventoryCompleted = true;
      clearGuestSetupCompleted = true;
      break;
    case "guest_setup":
      clearFromDrop();
      clearChecklistCompleted = true;
      clearInventoryCompleted = true;
      clearGuestSetupCompleted = true;
      break;
    case "inventory_single":
      clearFromDrop();
      clearChecklistCompleted = true;
      clearInventoryCompleted = true;
      break;
    case "after_photos":
      clearFromDrop();
      clearChecklistCompleted = true;
      break;
    case "drop_keys":
      clearFromDrop();
      break;
  }

  return { flowPatch, clearChecklistCompleted, clearInventoryCompleted, clearGuestSetupCompleted };
}

